// Shared word-matching helpers for the speak-along views (ReadingFlowView and
// LanguagePartnerView). Pure functions — no React, no DOM state.
import { romanize as romanizeKorean } from '@romanize/korean'

export const PUNCT_RE = /[.,!?;:"'“”‘’¿¡()«»、。，！？：；·…-]/g
export const NO_SPACE_LANGS = new Set(['cmn', 'jpn'])

// Split a line into displayable + matchable word tokens. For languages
// written without spaces we fall back to per-character tokens.
//
// Punctuation counts as a word boundary, not something to delete in place:
// models routinely emit "Нормально,спасибо" with no space after the comma, and
// stripping the comma would glue it into one token that spoken audio ("нормально
// спасибо" — two words) can never match.
export const tokenizeLine = (text, lang) => {
  if (!text) return []
  if (NO_SPACE_LANGS.has(lang)) {
    return Array.from(text.replace(/\s+/g, '').replace(PUNCT_RE, ''))
      .map(ch => ({ raw: ch, norm: ch }))
  }
  return text.split(/\s+/).filter(Boolean).flatMap(chunk => {
    const parts = chunk.replace(PUNCT_RE, ' ').split(/\s+/).filter(Boolean)
    if (parts.length === 0) return []                                  // pure punctuation
    if (parts.length === 1) return [{ raw: chunk, norm: parts[0].toLowerCase() }]
    return parts.map(p => ({ raw: p, norm: p.toLowerCase() }))          // was glued together
  })
}

export const tokenizeTranscript = (transcript, lang) => {
  if (NO_SPACE_LANGS.has(lang)) {
    return Array.from(transcript.replace(/\s+/g, '').replace(PUNCT_RE, ''))
  }
  return transcript.replace(PUNCT_RE, ' ').split(/\s+/).map(w => w.toLowerCase()).filter(Boolean)
}

// Small edit-distance so near-misses from the recognizer still count
// (e.g. accents, single-letter endings).
export const editDistance = (a, b) => {
  if (a === b) return 0
  const m = a.length, n = b.length
  if (Math.abs(m - n) > 2) return 99
  const row = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    let prev = row[0]
    row[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = row[j]
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = tmp
    }
  }
  return row[n]
}

// Deliberately forgiving: a learner mispronouncing an ending, or a recognizer
// dropping a soft sign ("фестивал" for "фестиваль") or hearing "будут" for
// "буду", should still count as having said the word.
export const tokenMatches = (spoken, expected) => {
  if (!expected || !spoken) return false
  if (spoken === expected) return true
  // Inflection / clipped-ending tolerance.
  const [shorter, longer] = spoken.length < expected.length ? [spoken, expected] : [expected, spoken]
  if (shorter.length >= 4 && longer.startsWith(shorter)) return true
  const d = editDistance(spoken, expected)
  if (expected.length >= 6) return d <= 2
  if (expected.length >= 3) return d <= 1
  return false
}

// Unbounded edit distance — `editDistance` bails early on length gaps, which is
// right for single words but useless for comparing whole phrases.
const fullDistance = (a, b) => {
  const m = a.length, n = b.length
  if (!m) return n
  if (!n) return m
  const row = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    let prev = row[0]
    row[0] = i
    for (let j = 1; j <= n; j++) {
      const tmp = row[j]
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1))
      prev = tmp
    }
  }
  return row[n]
}

// Safety net for per-word matching: recognizers hand back the right words with
// different spacing, punctuation or joins. If the final transcript is within
// ~20% of the whole target, count the line as said rather than making someone
// repeat a phrase they got right.
export const phraseCloseEnough = (transcript, target, lang) => {
  const a = tokenizeTranscript(transcript || '', lang).join(' ')
  const b = tokenizeLine(target || '', lang).map(t => t.norm).filter(Boolean).join(' ')
  if (!a || !b) return false
  if (a === b) return true
  return fullDistance(a, b) <= Math.max(1, Math.floor(b.length * 0.2))
}

// Catch a model answering in the wrong script entirely — qwen in particular
// likes to drop Chinese into a Russian sentence.
const CJK_RE = /[　-〿㐀-䶿一-鿿＀-￯]/
const CJK_LANGS = new Set(['cmn', 'jpn', 'kor'])
export const looksWrongScript = (text, lang) =>
  !CJK_LANGS.has(lang) && CJK_RE.test(String(text || ''))

export const romanizationOf = (text, lang) => {
  if (lang !== 'kor' || !text) return null
  try { return romanizeKorean(text) } catch { return null }
}

// TTS holds the audio session; starting recognition while it talks makes the
// session die instantly on Safari/some Chrome versions. Silence it first.
export const cancelTts = () => {
  try { window.speechSynthesis?.cancel() } catch { /* fine */ }
}

// Merge a cumulative recognition transcript into per-word states.
// Hits are sticky so a recognizer restart can't un-hit words.
//
// Matching is order-independent: if you say a word at all, it counts, wherever
// it lands. A fumbled or skipped word can't stall the rest of the line — the
// old forward-only window meant one bad word near the start could block
// everything after it.
export const mergeTranscript = (prevStates, transcript, words, lang, isFinal) => {
  const tokens = tokenizeTranscript(transcript, lang)
  const next = [...prevStates]
  let cursor = 0
  for (const tok of tokens) {
    let best = -1
    let bestDistance = Infinity
    for (let j = 0; j < next.length; j++) {
      if (next[j] === 'hit') continue
      if (!tokenMatches(tok, words[j]?.norm)) continue
      // Prefer the candidate nearest where we're reading, so repeated words
      // fill left-to-right, but allow a match anywhere in the line.
      const d = Math.abs(j - cursor)
      if (d < bestDistance) { bestDistance = d; best = j }
    }
    if (best >= 0) { next[best] = 'hit'; cursor = best + 1 }
  }
  if (isFinal) {
    const lastHit = next.lastIndexOf('hit')
    for (let k = 0; k < lastHit; k++) {
      if (next[k] === 'pending') next[k] = 'miss'
    }
  }
  return next
}

// You've said enough of the line to move on. Demanding every single word be
// perfect traps people on one stubborn word; ~70% (and never more than two
// short) is plenty to prove you said it.
export const lineIsGoodEnough = (states) => {
  if (!states || states.length === 0) return false
  const hits = states.filter(s => s === 'hit').length
  const needed = Math.max(1, Math.min(states.length, Math.ceil(states.length * 0.7)))
  return hits >= needed && (states.length - hits) <= 2
}
